<template>
    <div class="business-listing">
        <!-- Search and Filters -->
        <!-- <div class="filters mb-4">
            <div class="row">
                <div class="col-md-4">
                    <input
                        type="text"
                        class="form-control"
                        v-model="search"
                        placeholder="Search businesses..."
                        @input="debouncedSearch"
                    >
                </div>
                <div class="col-md-3">
                    <select class="form-control" v-model="sortBy" @change="resetAndFetch">
                        <option value="business_name">Sort by Name</option>
                        <option value="created_at">Newest First</option>
                        <option value="rating">Highest Rated</option>
                    </select>
                </div>
            </div>
        </div> -->

        <!-- Loading State (Initial) -->
        <div v-if="loading && !hasMoreData" class="text-center py-5">
            <div class="spinner-border" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2">Loading businesses...</p>
        </div>

        <!-- Business Grid -->
        <div v-if="!loading || businesses.length > 0">
            <div class="row">
                <div class="col-lg-4 col-md-4 col-sm-6" v-for="business in businesses" :key="business.id">
                    <div class="card aos aos-init aos-animate" data-aos="fade-up">
                        <div class="blog-widget">
                            <div class="blog-img">
                                <a :href="'/business/' + business.location?.city_id + '/' + business.slug" class="">
                                    <img
                                        v-if="business.featured_banner && business.featured_banner.image"
                                        :src="business.featured_banner.image"
                                        class="img-fluid"
                                        :alt="business.featured_banner.title"
                                        @error="handleImageError"
                                    >
                                    <div v-else class="banner-placeholder">
                                        <div class="placeholder-content">
                                            <i class="fa-regular fa-image"></i>
                                            <span>{{ business.business_name || 'MYBN' }}</span>
                                        </div>
                                    </div>
                                </a>
                                <div class="fav-item">
                                    <span class="featured-text" v-if="business.is_featured">Featured</span>
                                    <a href="javascript:void(0)" class="fav-icon"><i class="fa-regular fa-heart"></i></a>
                                </div>
                            </div>
                            <div class="bloglist-content">
                                <div class="card-body">
                                    <div class="blogfeaturelink">
                                        <div class="grid-author">
                                            <img v-if="business.profilepic"
                                                :src="business.profilepic"
                                                :alt="business.business_name"
                                                @error="handleImageError">
                                        </div>
                                        <div class="blog-features">
                                            <a href="javascript:void(0)"><span><i class="fa-regular fa-circle-stop"></i> {{ business.managed_by }}</span></a>
                                        </div>
                                        <div class="blog-author text-end">
                                            <span><i class="fa-regular fa-eye"></i>{{ business.stats.total_views || business.id * Math.floor(Math.random() * 40) }}</span>
                                        </div>
                                    </div>
                                    <h3>
                                        <a :href="'/business/' + business.location?.city_id + '/' + business.slug" class="">{{ business.business_name }}</a>
                                    </h3>
                                    <div class="blog-location-details">
                                        <div class="location-info">
                                            <i class="fa fa-map-marker"></i>
                                            {{ business.location?.city_name || business.city }}, {{ business.location?.state_name || business.state }}
                                        </div>
                                        <div class="location-info">
                                            <i class="fa-solid fa-calendar-days"></i>
                                            {{ $formatDate(business.updated_at, 'displayDate') }}
                                        </div>
                                    </div>
                                    <div class="business-description mt-2">
                                        <p class="text-muted">{{ truncate(business.description, 100) }}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Loading More Indicator -->
            <div v-if="loadingMore" class="text-center py-4">
                <div class="spinner-border spinner-border-sm" role="status">
                    <span class="visually-hidden">Loading more...</span>
                </div>
                <p class="mt-2 text-muted">Loading more businesses...</p>
            </div>

            <!-- No Results -->
            <div v-if="!loading && businesses.length === 0" class="text-center py-5">
                <div class="empty-state">
                    <i class="fas fa-store-slash fa-3x text-muted mb-3"></i>
                    <h4>No businesses found</h4>
                    <a :href="'/business'" class="">Back</a>
                    <!-- <p class="text-muted">Try adjusting your search or filters</p>
                    <button @click="resetFilters" class="btn btn-outline-primary mt-2">
                        Reset Filters
                    </button> -->
                </div>
            </div>

            <!-- End of Results -->
            <div v-if="!hasMoreData && businesses.length > 0 && !loading" class="text-center py-4">
                <div class="end-of-results">
                    <i class="fas fa-check-circle text-success"></i>
                    <p class="text-muted mt-2">You've seen all {{ businesses.length }} businesses</p>
                </div>
            </div>

            <!-- Scroll Sentinel (Invisible element for intersection observer) -->
            <div ref="scrollSentinel" class="scroll-sentinel"></div>
        </div>
    </div>
</template>

<script>
import ApiService from '../services/ApiService';

export default {
    name: 'BusinessList',

    props: {
        initialCategory: {
            type: String,
            required: true
        },
        perPage: {
            type: Number,
            default: 12 // Increased for better infinite scroll experience
        }
    },

    data() {
        return {
            businesses: [],
            categories: [],
            search: '',
            sortBy: 'business_name',
            sortOrder: 'desc',
            loading: false,
            loadingMore: false,
            currentPage: 1,
            lastPage: 1,
            total: 0,
            hasMoreData: true,
            observer: null,
            searchTimeout: null
        };
    },

    computed: {
        categorySlug() {
            return this.initialCategory;
        }
    },

    mounted() {
        this.fetchCategories();
        this.fetchBusinesses();

        // Setup intersection observer for infinite scroll
        this.setupInfiniteScroll();

        // Handle window scroll for older browsers
        window.addEventListener('scroll', this.handleScroll);
    },

    beforeUnmount() {
        // Cleanup
        if (this.observer) {
            this.observer.disconnect();
        }
        window.removeEventListener('scroll', this.handleScroll);
        clearTimeout(this.searchTimeout);
    },

    methods: {
        setupInfiniteScroll() {
            // Use Intersection Observer API for modern browsers
            if ('IntersectionObserver' in window) {
                this.observer = new IntersectionObserver(
                    (entries) => {
                        if (entries[0].isIntersecting && this.hasMoreData && !this.loadingMore) {
                            this.loadMore();
                        }
                    },
                    {
                        rootMargin: '100px', // Load when 100px from bottom
                        threshold: 0.1
                    }
                );

                // Start observing the sentinel element
                this.$nextTick(() => {
                    if (this.$refs.scrollSentinel) {
                        this.observer.observe(this.$refs.scrollSentinel);
                    }
                });
            }
        },

        handleScroll() {
            // Fallback for browsers without Intersection Observer
            if (!this.hasMoreData || this.loadingMore) return;

            const sentinel = this.$refs.scrollSentinel;
            if (!sentinel) return;

            const rect = sentinel.getBoundingClientRect();
            const windowHeight = window.innerHeight || document.documentElement.clientHeight;

            // Load more when sentinel is within 200px of viewport bottom
            if (rect.top <= windowHeight + 200) {
                this.loadMore();
            }
        },

        async fetchCategories() {
            try {
                const response = await ApiService.categories.getAll();
                this.categories = response.data;
            } catch (error) {
                console.error('Error fetching categories:', error);
            }
        },

        async fetchBusinesses(reset = true) {
            if (reset) {
                this.loading = true;
                this.currentPage = 1;
                this.hasMoreData = true;
            } else {
                this.loadingMore = true;
            }

            try {
                const params = {
                    page: this.currentPage,
                    per_page: this.perPage,
                    search: this.search,
                    category_id: this.categorySlug,
                    sort_by: this.sortBy,
                    sort_order: this.sortOrder
                };

                const response = await ApiService.businesses.getAll(params);

                if (reset) {
                    this.businesses = response.data.data || [];
                } else {
                    // Append new businesses to existing list
                    this.businesses = [...this.businesses, ...(response.data.data || [])];
                }

                // Update pagination info
                const pagination = response.data.pagination;
                this.currentPage = pagination.current_page;
                this.lastPage = pagination.last_page;
                this.total = pagination.total;

                // Check if there's more data
                this.hasMoreData = this.currentPage < this.lastPage;

                // Update URL without page reload (only on initial load or search)
                if (reset) {
                    this.updateUrl(params);
                }

            } catch (error) {
                console.error('Error fetching businesses:', error);
                if (reset) {
                    this.businesses = [];
                }
            } finally {
                this.loading = false;
                this.loadingMore = false;
            }
        },

        debouncedSearch() {
            clearTimeout(this.searchTimeout);
            this.searchTimeout = setTimeout(() => {
                this.resetAndFetch();
            }, 500);
        },

        resetAndFetch() {
            this.resetScrollPosition();
            this.fetchBusinesses(true);
        },

        async loadMore() {
            if (!this.hasMoreData || this.loadingMore) return;

            this.currentPage++;
            await this.fetchBusinesses(false);
        },

        updateUrl(params) {
            const url = new URL(window.location);
            Object.keys(params).forEach(key => {
                if (params[key] && key !== 'page') { // Don't include page in URL
                    url.searchParams.set(key, params[key]);
                } else {
                    url.searchParams.delete(key);
                }
            });

            window.history.pushState({}, '', url);
        },

        truncate(text, maxLength = 100) {
            if (!text || typeof text !== 'string') return '';

            if (text.length <= maxLength) return text;

            const truncated = text.substr(0, maxLength);
            const lastSpace = truncated.lastIndexOf(' ');

            if (lastSpace > 0 && lastSpace > maxLength - 20) {
                return truncated.substr(0, lastSpace) + '...';
            }

            return truncated + '...';
        },

        handleImageError(event) {
            event.target.src = 'https://listee.dreamstechnologies.com/vue/assets/listgrid-1-C35U96GW.jpg';
        },

        resetFilters() {
            this.search = '';
            this.sortBy = 'business_name';
            this.sortOrder = 'desc';
            this.resetAndFetch();
        },

        resetScrollPosition() {
            // Reset to top when searching or filtering
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }
};
</script>

<style scoped>
.business-listing {
    min-height: 400px;
    position: relative;
}

.card {
    transition: transform 0.2s;
    margin-bottom: 24px;
    height: 100%;
}

.card:hover {
    transform: translateY(-5px);
    box-shadow: 0 5px 15px rgba(0,0,0,0.1);
}

.spinner-border {
    width: 3rem;
    height: 3rem;
}

.spinner-border-sm {
    width: 1.5rem;
    height: 1.5rem;
}

.empty-state {
    padding: 3rem;
    background: #f8f9fa;
    border-radius: 10px;
}

.end-of-results {
    padding: 1.5rem;
    background: #f8f9fa;
    border-radius: 8px;
    border: 2px dashed #dee2e6;
}

.end-of-results i {
    font-size: 2rem;
}

.scroll-sentinel {
    height: 1px;
    width: 100%;
    visibility: hidden;
    margin: 20px 0;
}

.blog-img img {
    width: 100%;
    height: 200px;
    object-fit: cover;
}

.banner-placeholder {
    width: 100%;
    height: 100%;
    min-height:200px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgb(135, 212, 251);
}

.placeholder-content {
    text-align: center;
    padding: 20px;
}

.placeholder-content i {
    font-size: 3rem;
    margin-bottom: 10px;
    opacity: 0.8;
}

.placeholder-text {
    display: block;
    font-size: 1rem;
    font-weight: 500;
    opacity: 0.9;
    max-width: 200px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

/* Responsive adjustments */
@media (max-width: 768px) {
    .col-lg-4, .col-md-4, .col-sm-6 {
        margin-bottom: 20px;
    }

    .filters .row > div {
        margin-bottom: 15px;
    }
}

/* Loading animation */
@keyframes fadeIn {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
}

.card {
    animation: fadeIn 0.5s ease-out;
}
</style>